import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, TextField, Button, Grid,
  MenuItem, FormControlLabel, Checkbox, FormGroup, FormLabel, Fade,
  Alert, CircularProgress,
} from '@mui/material';
import SubmitIcon from '@mui/icons-material/CheckCircleOutlined';
import BackIcon from '@mui/icons-material/ChevronLeft';

const roles = [
  { value: 'clinician', label: 'Medical Doctor / Clinician' },
  { value: 'radiologist', label: 'Radiologist' },
  { value: 'pathologist', label: 'Pathologist' },
  { value: 'executive', label: 'Healthcare Executive / VP' },
  { value: 'researcher', label: 'Biotech Researcher / PhD' },
  { value: 'other', label: 'Other Technology Partner' },
];

const DemoRequest = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    organization: '',
    role: '',
    interests: { acoustic: false, radiology: false, pathology: false, multimodal: true },
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      interests: { ...prev.interests, [name]: checked },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.detail || 'Failed to submit request. Please try again.');
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again shortly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink">
        <Container maxWidth="sm" sx={{ py: 10 }}>
          <Fade in={true} timeout={600}>
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: 3,
                boxShadow: '4px 4px 0 0 rgba(20,184,166,0.18)',
                border: '1px solid #e2e8f0',
              }}
            >
              <Box sx={{ fontSize: 60, mb: 2 }}>🚀</Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#187F73' }}>
                Request Received
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Thank you, <strong>{formData.fullName}</strong>. A technical architect will contact you at <strong>{formData.email}</strong> within 24 hours.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/demo')}
                sx={{
                  mt: 3,
                  px: 4,
                  backgroundColor: '#187F73',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#15695F',
                  },
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                }}
              >
                Return to Demo
              </Button>
            </Paper>
          </Fade>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink">
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/diagnostic-core")}
          sx={{
            mb: 3,
            fontWeight: 'semibold',
            color: '#187F73',
            '&:hover': {
              backgroundColor: 'rgba(24,127,115,0.08)',
            },
          }}
        >
          Back to Interactive Workspace
        </Button>

        <div className="text-center mb-6">
          <div className="inline-block mb-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
              Enterprise Evaluation
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold leading-tight tracking-tight text-center text-teal">
            Request Enterprise Evaluation Platform
          </h1>
          <p className="mt-3 text-inkSoft">
            Provide your background profile to receive customized sandbox workspace instances.
          </p>
        </div>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            boxShadow: '4px 4px 0 0 rgba(20,184,166,0.18)',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '6px 6px 0 0 rgba(20,184,166,0.3)',
              transform: 'translate(-2px, -2px)',
            },
          }}
        >
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  disabled={submitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#187F73',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#187F73',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#187F73',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label="Institutional Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={submitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#187F73',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#187F73',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#187F73',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Organization / Medical Facility"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  disabled={submitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#187F73',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#187F73',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#187F73',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  select
                  fullWidth
                  label="Professional Role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={submitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#187F73',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#187F73',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#187F73',
                    },
                  }}
                >
                  {roles.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{
                  p: 3,
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid #edf2f7',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#187F73',
                  },
                }}>
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                    Target Platform Systems of Interest
                  </FormLabel>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.interests.acoustic}
                          onChange={handleCheckboxChange}
                          name="acoustic"
                          disabled={submitting}
                          sx={{
                            color: '#187F73',
                            '&.Mui-checked': {
                              color: '#187F73',
                            },
                          }}
                        />
                      }
                      label="Acoustic Intelligence"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.interests.radiology}
                          onChange={handleCheckboxChange}
                          name="radiology"
                          disabled={submitting}
                          sx={{
                            color: '#187F73',
                            '&.Mui-checked': {
                              color: '#187F73',
                            },
                          }}
                        />
                      }
                      label="Radiology Intelligence"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.interests.pathology}
                          onChange={handleCheckboxChange}
                          name="pathology"
                          disabled={submitting}
                          sx={{
                            color: '#187F73',
                            '&.Mui-checked': {
                              color: '#187F73',
                            },
                          }}
                        />
                      }
                      label="Pathology Intelligence"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.interests.multimodal}
                          onChange={handleCheckboxChange}
                          name="multimodal"
                          disabled
                          sx={{
                            color: '#187F73',
                            '&.Mui-checked': {
                              color: '#187F73',
                            },
                          }}
                        />
                      }
                      label="Multimodal Joint Fusion (CTF)"
                    />
                  </FormGroup>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Clinical Deployment Objectives"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  disabled={submitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#187F73',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#187F73',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#187F73',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SubmitIcon />}
                  disabled={submitting}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    backgroundColor: '#187F73',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#15695F',
                    },
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    '&:disabled': {
                      backgroundColor: '#94a3b8',
                      color: '#cbd5e1',
                    },
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Demo Request'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </div>
  );
};

export default DemoRequest;
